import { Box, Stack, Typography, Avatar, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplyIcon from "@mui/icons-material/Reply";
import { RichTextField } from "react-admin";
import { useState } from "react";
import { stringToColor } from "@/components/common/fields/AvatarField";
import { DisplayMode, type User } from "@/types";
import type { Comment } from "@/types/comment";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import Button from "@/components/ui/Button";
import { ColorType, ConfirmLabelType, UIComponentSize } from "@/types/shared/uiComponents";
import { getUserDisplayInfo } from "@/resources/tasks/utils/taskUtils";
import { formatCommentDate } from "@/resources/tasks/utils/commentUtils";
import { CommentInput } from "@/resources/tasks/components/task-comments/CommentInput";

interface CommentItemProps {
  comment: Comment;
  isCurrentUserComment: boolean;
  user: User | null;
  canDeleteComment?: boolean;
  onDelete?: (commentId: string) => void;
  isDeletingThis?: boolean;
  /** Show the inline edit affordance (author-only); wired to onEdit. */
  canEditComment?: boolean;
  onEdit?: (commentId: string, content: string) => void | Promise<void>;
  isSavingEdit?: boolean;
  /** Show the Reply affordance (top-level comments only — one-level threading). */
  showReplyAction?: boolean;
  onReplyClick?: () => void;
}

/**
 * Renders a single polymorphic comment (ADR-052). Author identity is resolved by
 * the parent from `comment.created_by` (a user UUID) and passed in as `user`.
 * Supports inline edit (author-only) and a Reply affordance; the reply composer
 * and nested replies are owned by the parent thread (RecordComments).
 */
export const CommentItem = ({
  comment,
  isCurrentUserComment,
  user,
  canDeleteComment,
  onDelete,
  isDeletingThis,
  canEditComment,
  onEdit,
  isSavingEdit,
  showReplyAction,
  onReplyClick,
}: CommentItemProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.content);
  const { displayName, jobTitle, initials } = getUserDisplayInfo(user);
  const { bgColor, textColor } = user ? stringToColor(displayName) : { bgColor: "#ccc", textColor: "#666" };

  const startEdit = () => {
    setEditDraft(comment.content);
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await onEdit?.(comment.id, editDraft);
      setEditing(false); // close only after the edit actually persisted
    } catch {
      // keep the editor open with the user's text on failure
    }
  };

  return (
    <Box className="comment-item" sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: bgColor, color: textColor, fontSize: 14, flexShrink: 0 }}>
        {isCurrentUserComment ? "Me" : initials}
      </Avatar>

      <Box sx={{ maxWidth: "70%", minWidth: "200px" }}>
        {!isCurrentUserComment && (
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="caption" fontWeight={600} color="text.primary">
              {displayName}
            </Typography>
            {jobTitle && (
              <Typography variant="caption" color="text.secondary">
                • {jobTitle}
              </Typography>
            )}
          </Stack>
        )}

        {editing ? (
          <Box>
            <CommentInput
              value={editDraft}
              onChange={setEditDraft}
              onSubmit={saveEdit}
              disabled={!!isSavingEdit}
              compact
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Button
                variant="text"
                size={UIComponentSize.Small}
                onClick={() => setEditing(false)}
                disabled={!!isSavingEdit}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: (theme) =>
                isCurrentUserComment
                  ? "primary.light"
                  : theme.palette.mode === DisplayMode.LIGHT
                    ? "background.default"
                    : "background.paper",
              color: isCurrentUserComment ? "primary.contrastText" : "text.primary",
              borderRadius: 3,
              px: 2,
              py: 1,
            }}
          >
            <Box
              sx={{
                fontSize: "0.875rem",
                wordBreak: "break-word",
                "& .ra-field-content": { fontSize: "inherit" },
                "& p": { m: 0 },
              }}
            >
              <RichTextField source="content" record={comment} emptyText="No content" />
            </Box>
          </Box>
        )}

        {!editing && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 0.5,
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
              {comment.created_datetime ? formatCommentDate(comment.created_datetime) : "-"}
            </Typography>
            {comment.is_edited && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                  edited
                </Typography>
              </>
            )}
            {showReplyAction && (
              <Button
                variant="text"
                size={UIComponentSize.Small}
                startIcon={<ReplyIcon sx={{ fontSize: 14 }} />}
                onClick={onReplyClick}
                sx={{
                  minWidth: 0,
                  py: 0,
                  px: 0.5,
                  fontSize: "0.7rem",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ".comment-item:hover &": { opacity: 0.7 },
                  "&:focus-visible": { opacity: 1 },
                  "&:hover": { opacity: "1 !important" },
                }}
              >
                Reply
              </Button>
            )}
            {canEditComment && (
              <IconButton
                size="small"
                onClick={startEdit}
                aria-label="Edit comment"
                sx={{
                  p: 0.25,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ".comment-item:hover &": { opacity: 0.6 },
                  "&:focus-visible": { opacity: 1 },
                  "&:hover": { opacity: "1 !important", color: "primary.main" },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
            {canDeleteComment && (
              <IconButton
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={isDeletingThis}
                aria-label="Delete comment"
                sx={{
                  p: 0.25,
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ".comment-item:hover &": { opacity: 0.6 },
                  "&:focus-visible": { opacity: 1 },
                  "&:hover": { opacity: "1 !important", color: "error.main" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={confirmOpen}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel={ConfirmLabelType.Delete}
        confirmColor={ColorType.Error}
        onConfirm={() => {
          setConfirmOpen(false);
          onDelete?.(comment.id);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
};
